import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { cn } from '@/lib/utils.js';

import {
  type OfficePreviewKind,
  TOO_LARGE_TO_PREVIEW,
} from './artifact-display.js';
import { NoPreview, PreviewLoadError } from './NoPreview.js';
import { type PagePosition, useOfficeViewer } from './useOfficeViewer.js';

/**
 * What a screen reader hears for the canvas, which has no text of its own: the file and, once known, the
 * page or slide it shows ("deck.pptx, slide 2 of 7"). Never the bare name, which labels the whole view.
 */
function pageLabel(
  name: string,
  kind: OfficePreviewKind,
  position: PagePosition,
): string {
  if (position.total === 0) return `${name}, loading`;
  const unit = kind === 'pptx' ? 'slide' : 'page';
  const total = `${position.total}${position.complete ? '' : '+'}`;
  return `${name}, ${unit} ${position.current + 1} of ${total}`;
}

/**
 * The bar under a paged document: previous, "Page 2 of 7", next. While a long document is still being laid
 * out the total is only what is known so far, so it reads "of 7+".
 */
function PageBar({
  unit,
  current,
  total,
  complete,
  onTurn,
}: {
  unit: 'page' | 'slide';
  current: number;
  total: number;
  complete: boolean;
  onTurn: (step: number) => void;
}): JSX.Element {
  const label = unit === 'page' ? 'Page' : 'Slide';
  return (
    <div className="flex shrink-0 items-center justify-center gap-2 border-t border-border bg-background py-1.5 text-xs text-muted-foreground">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Previous ${unit}`}
        disabled={current <= 0}
        onClick={() => onTurn(-1)}
      >
        <ChevronLeftIcon />
      </Button>
      <span>{`${label} ${current + 1} of ${total}${complete ? '' : '+'}`}</span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Next ${unit}`}
        disabled={current >= total - 1}
        onClick={() => onTurn(1)}
      >
        <ChevronRightIcon />
      </Button>
    </div>
  );
}

/**
 * A Word, Excel or PowerPoint file drawn by `@silurus/ooxml` onto canvases. Nothing from the file becomes
 * markup — the workbook's sheet names are the one exception, set as tab text — so a crafted document cannot
 * run anything. The renderer for each format is fetched only when such a file is opened. A file the renderer
 * rejects shows the load-error prompt; the size cap is applied before the download, by the caller.
 */
export function OfficeDocumentView({
  url,
  name,
  kind,
  fill = false,
}: {
  url: string;
  name: string;
  kind: OfficePreviewKind;
  fill?: boolean;
}): JSX.Element {
  const {
    hostRef,
    pageBoxRef,
    canvasRef,
    status,
    position,
    turn,
    slideAspect,
  } = useOfficeViewer(url, kind);
  /**
   * A deck that does not fill a fixed box takes the slide's own shape instead of a fixed height, so a tall
   * window shows the slide without bands of empty stage above and below it. Capped so the box, the bar and
   * the dialog's own header still fit on screen.
   */
  const hugSlide = kind === 'pptx' && !fill;
  if (status === 'error') return <PreviewLoadError />;
  if (status === 'too-large')
    return (
      <NoPreview name={name} contentType="" reason={TOO_LARGE_TO_PREVIEW} />
    );
  return (
    <div
      aria-label={name}
      aria-busy={status === 'loading'}
      className={cn(
        'relative flex w-full flex-col overflow-hidden rounded-lg border border-border',
        kind === 'xlsx' ? 'bg-white' : 'bg-muted/40',
        fill ? 'h-full' : !hugSlide && 'h-[70vh]',
      )}
    >
      {status === 'loading' && (
        <Skeleton className="absolute inset-0 z-10 rounded-lg" />
      )}
      <div
        ref={hostRef}
        className={cn(
          'min-h-0 flex-1',
          kind === 'xlsx' ? '' : 'overflow-auto p-3',
        )}
      >
        {kind !== 'xlsx' && (
          <div
            ref={pageBoxRef}
            className={cn(
              'w-full',
              kind === 'pptx'
                ? 'flex items-center justify-center'
                : 'mx-auto max-w-[960px] text-center',
              kind === 'pptx' && !hugSlide && 'h-full',
            )}
            style={
              hugSlide
                ? {
                    aspectRatio: slideAspect,
                    maxHeight: 'calc(var(--app-height,100dvh) - 17rem)',
                  }
                : undefined
            }
          >
            <canvas
              ref={canvasRef}
              role="img"
              aria-label={pageLabel(name, kind, position)}
              className="mx-auto block bg-white shadow-sm"
            />
          </div>
        )}
      </div>
      {kind !== 'xlsx' && position.total > 1 && (
        <PageBar
          unit={kind === 'pptx' ? 'slide' : 'page'}
          current={position.current}
          total={position.total}
          complete={position.complete}
          onTurn={(step) => void turn(step)}
        />
      )}
    </div>
  );
}
